import { Component, computed, inject, input, numberAttribute } from '@angular/core';

import { IconRegistry } from './icon-registry';
import type { IconName } from './icons';

/**
 * Draws an icon from `public/icons`, so templates never hand-roll `<svg>` markup:
 *
 * ```html
 * <app-icon name="arrow-right" />
 * <app-icon class="contact__list-arrow" name="arrow-right" />
 * <app-icon name="arrow-right" size="18" [rotate]="90" />
 * <app-icon name="close" label="Remove file" />
 * ```
 *
 * `name` is the file name in `public/icons` — see `icons.ts` for how to add one. The file is
 * inlined into the host element rather than loaded through `<img>`, which is what lets the artwork
 * inherit `color` and scale with CSS.
 *
 * The host element is the icon box, so a class on `<app-icon>` styles it like any other element.
 * Sizing is CSS-first: leave `size` unset and set `--icon-size` (the fallback is `1em`) from the
 * call site's stylesheet, which keeps responsive tweaks in the media queries where the rest of the
 * layout lives. Colour works the same way — the artwork draws with `currentColor`, so setting
 * `color` on the host or any ancestor recolours it.
 */
@Component({
  selector: 'app-icon',
  host: {
    class: 'icon',
    '[innerHTML]': 'content()',
    '[style.width]': 'dimension()',
    '[style.height]': 'dimension()',
    '[style.color]': 'color()',
    '[style.rotate]': 'rotation()',
    // Decorative by default; a label promotes the icon to a standalone image for screen readers.
    '[attr.role]': 'label() ? "img" : null',
    '[attr.aria-label]': 'label()',
    '[attr.aria-hidden]': 'label() ? null : "true"',
  },
  template: '',
})
export class Icon {
  /** File name of the artwork in `public/icons`, without the extension. */
  readonly name = input.required<IconName>();

  /** Overrides the CSS size. A number is read as pixels, a string as any CSS length (`1.5rem`). */
  readonly size = input<number | string | null>(null);

  /** Overrides the inherited `color`. Accepts any CSS colour, including `var(--token)`. */
  readonly color = input<string | null>(null);

  /** Accessible name. Leave unset for icons that only repeat adjacent text — they stay hidden. */
  readonly label = input<string | null>(null);

  /** Clockwise rotation in degrees, for reusing directional icons (an arrow as a chevron, …). */
  readonly rotate = input(0, { transform: numberAttribute });

  private readonly registry = inject(IconRegistry);

  protected readonly content = computed(() => this.registry.content(this.name())());

  protected readonly dimension = computed(() => {
    const size = this.size();
    if (size === null || size === '') {
      return null;
    }

    // A bare number — including the string a static template attribute yields — means pixels.
    const pixels = typeof size === 'number' ? size : Number(size);
    return Number.isFinite(pixels) ? `${pixels}px` : `${size}`;
  });

  protected readonly rotation = computed(() => {
    const degrees = this.rotate();
    return degrees ? `${degrees}deg` : null;
  });
}
